import React, { useEffect, useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Scale, TrendingDown } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { unctadApi } from '../../../services/api';
import type { TradeData } from '../../../services/api/unctadApi';

interface TradeBalancePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const TradeBalancePanel: React.FC<TradeBalancePanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<TradeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await unctadApi.getTradeData(['UZB']);
      if (response.data && response.data.length > 0) {
        setData(response.data[0]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const exportData = data ? [{
    exports: data.exports,
    imports: data.imports,
    tradeBalance: data.tradeBalance,
    year: data.year,
    source: data.source,
  }] : [];

  return (
    <Panel
      id="trade-balance"
      title="Trade Balance"
      subtitle="Uzbekistan"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col p-3">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            Loading trade data...
          </div>
        ) : data ? (
          <>
            {/* Hero - Trade Balance */}
            <div className="text-center py-5 px-4 bg-gradient-to-b from-data-red/10 to-transparent rounded-lg border border-data-red/20 mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Scale size={16} className="text-data-amber" />
                <span className="text-xs text-data-amber uppercase tracking-widest font-semibold">
                  Trade Balance
                </span>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                  data.tradeBalance < 0 ? 'bg-data-red/20 text-data-red' : 'bg-data-green/20 text-data-green'
                }`}>
                  DEFICIT
                </span>
              </div>
              <div className={`text-5xl font-mono font-black mb-2 ${
                data.tradeBalance < 0 ? 'text-data-red' : 'text-data-green'
              }`}>
                <span className="text-3xl">$</span>
                {Math.abs(data.tradeBalance)}
                <span className="text-3xl">B</span>
              </div>
              <div className="text-xs text-gray-500">{data.year}</div>
            </div>

            {/* Exports vs Imports */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Exports */}
              <div className="bg-data-green/10 rounded-lg p-4 border border-data-green/20">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight size={14} className="text-data-green" />
                  <span className="text-xs text-gray-400 uppercase">Exports</span>
                </div>
                <div className="text-3xl font-mono font-bold text-data-green">
                  ${data.exports}B
                </div>
              </div>

              {/* Imports */}
              <div className="bg-data-red/10 rounded-lg p-4 border border-data-red/20">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight size={14} className="text-data-red" />
                  <span className="text-xs text-gray-400 uppercase">Imports</span>
                </div>
                <div className="text-3xl font-mono font-bold text-data-red">
                  ${data.imports}B
                </div>
              </div>
            </div>

            {/* Visual Balance Bar */}
            <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30 mb-4">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">Trade Composition</div>
              <div className="flex h-5 bg-terminal-border rounded-full overflow-hidden mb-2">
                <div
                  className="bg-data-green flex items-center justify-center"
                  style={{ width: `${(data.exports / (data.exports + data.imports)) * 100}%` }}
                >
                  <span className="text-xs font-bold text-black">
                    {((data.exports / (data.exports + data.imports)) * 100).toFixed(0)}%
                  </span>
                </div>
                <div
                  className="bg-data-red flex items-center justify-center"
                  style={{ width: `${(data.imports / (data.exports + data.imports)) * 100}%` }}
                >
                  <span className="text-xs font-bold text-white">
                    {((data.imports / (data.exports + data.imports)) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-data-green">Exports</span>
                <span className="text-data-red">Imports</span>
              </div>
            </div>

            {/* Trade Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-terminal-bg/40 rounded-lg p-3 text-center border border-terminal-border/30">
                <div className="text-2xl font-mono font-bold text-white">
                  {((data.exports / data.imports) * 100).toFixed(0)}%
                </div>
                <div className="text-xs text-gray-500">Coverage Ratio</div>
              </div>
              <div className="bg-terminal-bg/40 rounded-lg p-3 text-center border border-terminal-border/30">
                <div className="text-2xl font-mono font-bold text-data-amber">
                  ${(data.exports + data.imports).toFixed(1)}B
                </div>
                <div className="text-xs text-gray-500">Total Trade</div>
              </div>
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
          <span className="text-xs text-gray-500">{data?.year || '2024'}</span>
        </div>
      </div>
    </Panel>
  );
};

export default TradeBalancePanel;
